import { getAttributeValueTuples, type Product } from '@scayle/storefront-nuxt'
import type { BreadcrumbItem } from '~/types/breadcrumbs'
import { toValue, type ComputedRef } from '#imports'
import type {
  RouteParams,
  RouteLocationNormalizedLoadedGeneric,
  RouteLocationNormalized,
} from '#vue-router'

export const getCategoryId = (route: RouteParams): number => {
  const id = Array.isArray(route.id) ? route.id[0] : route.id
  return parseInt(id, 10)
}

export const getProductId = (route: RouteParams): number => {
  const id = Array.isArray(route.id) ? route.id[0] : route.id
  return parseInt(id, 10)
}
export const normalizePathRoute = (path: string) => {
  return path.startsWith('/') ? path : `/${path}`
}

export const hasLocalePrefix = (path: string, prefix?: string) => {
  const components = normalizePathRoute(path).split('/')
  return components[1] && components[1] === prefix
}

export const isExternalLink = (link: string): boolean => {
  return typeof link === 'string' && link.startsWith('http')
}

export const generateCategoryBreadcrumbSchema = (
  breadcrumbs: BreadcrumbItem[],
) => ({
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: breadcrumbs.map((item, index) => ({
    '@type': 'ListItem',
    position: index + 1,
    name: item.value,
    item: item.to?.['@id'],
  })),
})
export const getBrand = (
  route: RouteLocationNormalizedLoadedGeneric,
  products?: ComputedRef<Product[]>,
): string => {
  if (
    route.params.categories &&
    route.params.categories.length > 0 &&
    route.params.categories[0].includes('brands') &&
    route.params.slug[0]
  ) {
    return Array.isArray(route.params.slug)
      ? route.params.slug[0]
      : route.params.slug
  }
  const brandFilterValue = route.query['filters[brand]'] as string
  const hasBrandFilter = Boolean(brandFilterValue)
  const hasOnlyOneBrandFilter =
    hasBrandFilter && brandFilterValue.split(',').length === 1
  if (products?.value && hasOnlyOneBrandFilter) {
    const _products = toValue(products)
    if (_products && _products.length > 0) {
      const brandAttribute = getAttributeValueTuples(
        _products[0].attributes,
        'brand',
      ).find((attribute) => attribute.id === Number(brandFilterValue))
      return brandAttribute?.label || ''
    }
  }
  return ''
}

type Link =
  | 'home'
  | 'checkout'
  | 'precheckout'
  | 'wishlist'
  | 'cart'
  | 'signin'
  | 'signinCallback'
  | 'signup'
  | 'profile'
  | 'orders'
  | 'account'
  | 'pdp'
  | 'brands'
  | 'brand'
  | 'category'
  | 'orderDetail'
  | 'search'
  | 'osp'
  | 'location'
  | 'subscriptionOverview'
  | 'subscriptionCancellations'
  | 'storefinder'

export type LinkList = Record<
  Link,
  {
    name: string
    path: string
    isProtected?: boolean
    parameter?: string
    query?: { [key: string]: string }
  }
>

export const routeList: LinkList = {
  home: { name: 'index', path: '/' },
  search: { name: 'search', path: '/search' },
  wishlist: { name: 'wishlist', path: '/wishlist' },
  category: { name: 'c-category-slug-id', path: '/c' },
  pdp: { name: 'p-productName-id', path: '/p' },
  osp: { name: 'success', path: '/success' },
  location: { name: 'location', path: '/location' },
  cart: { name: 'cart', path: '/cart' },
  signin: { name: 'signin', path: '/signin' },
  signinCallback: { name: 'signin-callback', path: '/signin/callback' },
  signup: { name: 'signup', path: '/signup' },
  checkout: { name: 'checkout', path: '/checkout', isProtected: true },
  precheckout: { name: 'precheckout', path: '/precheckout' },
  brands: { name: 'brands', path: '/s/brands' },
  brand: { name: 'brand', path: '/b' },
  storefinder: { name: 'storefinder', path: '/storefinder' },
  profile: {
    name: 'account-profile',
    path: '/account/profile',
    isProtected: true,
  },
  account: { name: 'account', path: '/account', isProtected: true },
  orders: {
    name: 'account-orders',
    path: '/account/orders',
    isProtected: true,
  },
  orderDetail: {
    name: 'account-orders-id',
    path: '/account/orders',
    isProtected: true,
  },
  subscriptionOverview: {
    name: 'subscription-overview',
    path: '/account/subscriptions',
    isProtected: true,
  },
  subscriptionCancellations: {
    name: 'subscription-cancellations',
    path: '/account/subscription-cancellations',
    isProtected: true,
  },
} as const

export const getProtectedRouteList = (
  exclude?: string,
): LinkList[keyof LinkList][] => {
  return Object.entries(routeList)
    .filter(([key, value]) => value.isProtected && exclude !== key)
    .map(([, route]) => route)
}

export const getPageType = (route: RouteLocationNormalized) => {
  if (!route?.meta?.SnPageType) {
    return 'other'
  }
  if (route.meta.SnPageType === 'homepage') {
    // Check if this is a gender page (men, women, kids)
    const pathSegments = route.path
      .split('/')
      .filter((segment) => segment.length > 0)
    const scope = pathSegments[1] // The second segment after locale (en-eu)

    // For root homepage (/ or /en-eu), return 'homepage'
    return scope ? 'genderpage' : 'homepage'
  } else {
    return route.meta.SnPageType
  }
}
